import type { AlertEvent } from '@apm/contracts';
import { useId, useState } from 'react';
import { useAccess } from '../auth/access';
import { routeHref } from '../router';
import { serviceLink } from '../services/service-filter';
import { ALERT_STATE_LABEL } from '../terms';
import { formatFull, formatSpan, formatStamp } from '../time';
import { Cell, Chip, type Column, Row, Table } from '../ui';
import {
  alertLinks,
  alertSpanMs,
  formatAlertValue,
  SEVERITY_LABEL,
  SEVERITY_TONE,
  signalLabel,
  suppressedNote,
} from './format';

/**
 * 알림 이력 표 (10회차 결정 5).
 *
 * 행 하나가 사건 하나다. 경보와 해제는 서버에서 이미 합쳐져 온다.
 * 오른쪽 링크로 그 요청의 상세·CPU 기록·검색으로 건너간다.
 *
 * 상태 아래의 "억제됨"은 쿨다운·분당 상한에 걸려 **밖으로 보내지만 않은** 것이다 (24회차).
 * 이력에는 남는다. 이 표시가 없으면 "그때 경보가 났었나"를 화면에서 알 길이 없다.
 */
const COLUMNS: readonly Column[] = [
  { label: '시각', width: 'w-40' },
  { label: '심각도', width: 'w-24' },
  { label: '상태', width: 'w-24' },
  { label: '규칙 · 대상' },
  { label: '값', width: 'w-28' },
  { label: '지속', width: 'w-28' },
  { label: '인스턴스', width: 'w-40' },
  { label: '이동', width: 'w-56' },
];

/** 상태별 글자 색. 통보(`notice`)는 경보가 아니라 회색이다 (24회차) */
const STATE_TONE = {
  firing: 'error',
  resolved: 'ok',
  notice: 'muted',
} as const;

export function AlertTable({
  rows,
  now,
  showService = false,
}: {
  rows: readonly AlertEvent[];
  now: number;
  showService?: boolean;
}) {
  const { admin } = useAccess();
  return (
    <Table
      columns={showService ? [{ label: '서비스', width: 'w-40' }, ...COLUMNS] : COLUMNS}
      caption="알림 이력"
      minWidth="min-w-[1080px]"
    >
      {rows.map((row) => (
        <AlertHistoryRow key={row.id} row={row} now={now} showService={showService} admin={admin} />
      ))}
    </Table>
  );
}

function AlertHistoryRow({
  row,
  now,
  showService,
  admin,
}: {
  row: AlertEvent;
  now: number;
  showService: boolean;
  admin: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const detailId = useId();
  return (
    <>
      <Row>
        {showService ? (
          <Cell title={row.service} className="max-w-[180px] truncate">
            {row.service ?? '전체 공통'}
          </Cell>
        ) : null}
        <Cell nowrap className="tabular" title={formatFull(row.startedAt)}>
          {formatStamp(row.startedAt)}
        </Cell>
        <Cell>
          <Chip tone={SEVERITY_TONE[row.severity]}>{SEVERITY_LABEL[row.severity]}</Chip>
        </Cell>
        <Cell tone={STATE_TONE[row.state]}>
          {ALERT_STATE_LABEL[row.state]}
          {suppressedNote(row) === undefined ? null : (
            <div
              className="text-xs text-muted"
              title="쿨다운·분당 상한에 걸려 채팅·로그로는 보내지 않았다"
            >
              {suppressedNote(row)}
            </div>
          )}
        </Cell>
        <Cell>
          <div className="break-words text-ink">{row.ruleName}</div>
          <div className="break-all text-muted">
            {signalLabel(row)} · {row.key}
          </div>
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={detailId}
            onClick={() => setExpanded((value) => !value)}
            className="mt-1 whitespace-nowrap text-xs text-link hover:underline"
          >
            {expanded ? '알림 내용 접기' : '알림 내용 보기'}
          </button>
        </Cell>
        <Cell mono nowrap className="tabular">
          {formatAlertValue(row)}
        </Cell>
        <Cell nowrap tone="muted" className="tabular">
          {formatSpan(alertSpanMs(row, now))}
        </Cell>
        <Cell mono tone="muted" title={row.instance} className="max-w-[180px] truncate">
          {row.instance ?? '-'}
        </Cell>
        <Cell>
          <span className="flex flex-wrap gap-1.5">
            {alertLinks(row).map((link) => (
              <a
                key={link.label}
                {...serviceLink(row.service, routeHref(link.route))}
                className="rounded-md border border-line px-2 py-1 text-link no-underline transition-colors duration-150 hover:bg-band"
              >
                {link.label}
              </a>
            ))}
            {admin && row.signal === 'server_error' ? (
              <a
                href={routeHref({ name: 'admin', sub: 'server' })}
                className="rounded-md border border-line px-2 py-1 text-link no-underline transition-colors duration-150 hover:bg-band"
              >
                관제 서버 진단
              </a>
            ) : null}
          </span>
        </Cell>
      </Row>
      {expanded ? (
        <Row>
          <Cell colSpan={COLUMNS.length + (showService ? 1 : 0)} className="bg-band">
            <div id={detailId}>
              <p className="whitespace-pre-wrap break-all text-ink">{row.message}</p>
              {row.state === 'notice' ? (
                <p className="mt-2 text-muted">
                  통보는 복구를 뜻하지 않는다. 종료 이유와 현재 상태는 알림 내용을 확인할 것
                </p>
              ) : null}
            </div>
          </Cell>
        </Row>
      ) : null}
    </>
  );
}
